import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  Container,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { languageMappping } from "../../languages/languages";
import {
  ExistingTermsAcceptances,
  resolveTermsApplicant,
  submitTermsAcceptance,
} from "../../data-functions/terms-acceptance-api";
import { ApplicantDoc } from "../../types/applicants";
import {
  getTermsHtmlPath,
  resolveTermsLanguage,
  TERMS_DOCUMENTS,
  TERMS_LANGUAGES,
  TermsDocumentType,
  TermsLanguage,
} from "../../types/terms-acceptance";

type PageStep = "email" | "gwp_terms" | "hops_privacy_policy" | "done";
type GwpConsentKey = "terms" | "dataProtection" | "govUkHelper" | "newServices";
type RequiredGwpConsentKey = Exclude<GwpConsentKey, "newServices">;

const REQUIRED_GWP_CONSENT_KEYS: RequiredGwpConsentKey[] = [
  "terms",
  "dataProtection",
  "govUkHelper",
];
const GWP_SECTION_TITLES = [
  "Applicant Consent & Agreement",
  "Согласие и подтверждение заявителя",
];

const normalizeTextContent = (value: string | null | undefined) =>
  (value || "").replace(/\s+/g, " ").trim();

const PAGE_COPY: Record<
  TermsLanguage,
  {
    pageTitle: string;
    pageDescription: string;
    emailLabel: string;
    emailButton: string;
    emailHelp: string;
    emailNotFound: string;
    genericError: string;
    documentLoadError: string;
    acceptanceError: string;
    recognizedApplicant: string;
    languageLabel: string;
    acceptedEarlier: string;
    scrollToEndHint: string;
    continueButton: string;
    finishButton: string;
    gwpRequiredCheckboxes: Record<RequiredGwpConsentKey, string>;
    gwpOptionalHeading: string;
    gwpOptionalCheckbox: string;
    hopsCheckbox: string;
    confirmationTitle: string;
    confirmationDescription: string;
    confirmationCooperation: string;
    confirmationRecorded: string;
  }
> = {
  en: {
    pageTitle: "Privacy Policy / Terms Acceptance",
    pageDescription:
      "Enter your email address to review and accept the required documents.",
    emailLabel: "Email address",
    emailButton: "Continue",
    emailHelp:
      "Use the same email address that exists in the application system.",
    emailNotFound: "We could not find this email address in the system.",
    genericError: "Something went wrong. Please try again.",
    documentLoadError: "The selected document could not be loaded.",
    acceptanceError: "We could not save your acceptance. Please try again.",
    recognizedApplicant: "Email found in the system for",
    languageLabel: "Language",
    acceptedEarlier: "Previously accepted on",
    scrollToEndHint:
      "Please scroll to the end of the document before accepting.",
    continueButton: "Accept and continue",
    finishButton: "Accept and finish",
    gwpRequiredCheckboxes: {
      terms:
        "Terms & Conditions: I have read, understood, and agree to the Terms and Conditions of GWP - Global Workforce Platform as set out in this document.",
      dataProtection:
        "Data Protection Statement: I have read and understood GWP's Data Protection Statement and consent to the collection and processing of my personal data as described therein for the purpose of my UK visa application.",
      govUkHelper:
        "gov.uk Helper Authorisation: I authorise GWP to register as my Helper on the gov.uk platform for the purpose of my visa application. I understand that I may change my sign-in details and/or remove GWP as my Helper at any time via my gov.uk account settings.",
    },
    gwpOptionalHeading: "New Services Notification",
    gwpOptionalCheckbox:
      "New Services Notification - optional: Yes, I agree to be contacted by GWP about new services, updates, and relevant information for UK Visa. I understand that I may withdraw this consent at any time by contacting GWP at dataprotection@global-workforce.app or by clicking 'unsubscribe' in any communication received.",
    hopsCheckbox: "I have read and accept this Privacy Policy.",
    confirmationTitle: "Thank you",
    confirmationDescription: "Thank you for your cooperation.",
    confirmationCooperation:
      "Your acceptance of the required documents has been recorded successfully.",
    confirmationRecorded: "Accepted documents",
  },
  bg: {
    pageTitle: "Приемане на условия и политика за поверителност",
    pageDescription:
      "Въведете своя имейл адрес, за да прегледате и приемете изискуемите документи.",
    emailLabel: "Имейл адрес",
    emailButton: "Продължи",
    emailHelp: "Използвайте същия имейл адрес, който съществува в системата.",
    emailNotFound: "Не открихме този имейл адрес в системата.",
    genericError: "Възникна грешка. Моля, опитайте отново.",
    documentLoadError: "Избраният документ не можа да бъде зареден.",
    acceptanceError: "Не успяхме да запазим приемането. Моля, опитайте отново.",
    recognizedApplicant: "Имейлът е намерен в системата за",
    languageLabel: "Език",
    acceptedEarlier: "Прието по-рано на",
    scrollToEndHint: "Моля, превъртете до края на документа преди да приемете.",
    continueButton: "Приеми и продължи",
    finishButton: "Приеми и завърши",
    gwpRequiredCheckboxes: {
      terms:
        "Условия и положения: Я прочитал(а), понял(а) и согласен(на) с Условиями и положениями GWP - Global Workforce Platform, изложенными в настоящем документе.",
      dataProtection:
        "Заявление о защите данных: Я прочитал(а) и понял(а) Заявление GWP о защите данных и даю согласие на сбор и обработку моих персональных данных, описанных в нём, в целях подачи заявления на визу в Великобританию.",
      govUkHelper:
        "Авторизация GWP в качестве Помощника на gov.uk: Я уполномочиваю GWP зарегистрироваться в качестве моего Помощника на платформе gov.uk в целях подачи заявления на визу. Я понимаю, что могу в любое время изменить данные для входа и/или удалить GWP из списка Помощников через настройки учётной записи на gov.uk.",
    },
    gwpOptionalHeading: "Уведомления о новых услугах - необязательно",
    gwpOptionalCheckbox:
      "Уведомления о новых услугах - необязательно: Да, я согласен(на) получать от GWP информацию о новых услугах, обновлениях и актуальных сведениях по вопросам визы в Великобританию. Я понимаю, что могу отозвать это согласие в любое время, обратившись в GWP по адресу dataprotection@global-workforce.app или нажав 'отписаться' в любом полученном сообщении.",
    hopsCheckbox: "Прочетох и приемам тази Политика за поверителност.",
    confirmationTitle: "Благодарим",
    confirmationDescription: "Благодарим Ви за съдействието.",
    confirmationCooperation:
      "Приемането на изискуемите документи беше записано успешно.",
    confirmationRecorded: "Приети документи",
  },
  ru: {
    pageTitle: "Принятие условий и политики конфиденциальности",
    pageDescription:
      "Введите свой адрес электронной почты, чтобы просмотреть и принять необходимые документы.",
    emailLabel: "Адрес электронной почты",
    emailButton: "Продолжить",
    emailHelp:
      "Используйте тот же адрес электронной почты, который уже есть в системе.",
    emailNotFound: "Мы не нашли этот адрес электронной почты в системе.",
    genericError: "Что-то пошло не так. Пожалуйста, попробуйте еще раз.",
    documentLoadError: "Не удалось загрузить выбранный документ.",
    acceptanceError:
      "Не удалось сохранить принятие. Пожалуйста, попробуйте еще раз.",
    recognizedApplicant: "Адрес найден в системе для",
    languageLabel: "Язык",
    acceptedEarlier: "Ранее принято",
    scrollToEndHint:
      "Пожалуйста, прокрутите документ до конца перед принятием.",
    continueButton: "Принять и продолжить",
    finishButton: "Принять и завершить",
    gwpRequiredCheckboxes: {
      terms:
        "Условия и положения: Я прочитал(а), понял(а) и согласен(на) с Условиями и положениями GWP - Global Workforce Platform, изложенными в настоящем документе.",
      dataProtection:
        "Заявление о защите данных: Я прочитал(а) и понял(а) Заявление GWP о защите данных и даю согласие на сбор и обработку моих персональных данных, описанных в нём, в целях подачи заявления на визу в Великобританию.",
      govUkHelper:
        "Авторизация GWP в качестве Помощника на gov.uk: Я уполномочиваю GWP зарегистрироваться в качестве моего Помощника на платформе gov.uk в целях подачи заявления на визу. Я понимаю, что могу в любое время изменить данные для входа и/или удалить GWP из списка Помощников через настройки учётной записи на gov.uk.",
    },
    gwpOptionalHeading: "Уведомления о новых услугах - необязательно",
    gwpOptionalCheckbox:
      "Уведомления о новых услугах - необязательно: Да, я согласен(на) получать от GWP информацию о новых услугах, обновлениях и актуальных сведениях по вопросам визы в Великобританию. Я понимаю, что могу отозвать это согласие в любое время, обратившись в GWP по адресу dataprotection@global-workforce.app или нажав 'отписаться' в любом полученном сообщении.",
    hopsCheckbox: "Я прочитал(а) и принимаю эту Политику конфиденциальности.",
    confirmationTitle: "Спасибо",
    confirmationDescription: "Спасибо за ваше сотрудничество.",
    confirmationCooperation:
      "Принятие обязательных документов было успешно сохранено.",
    confirmationRecorded: "Принятые документы",
  },
};

const stripTrailingGwpConsentSection = (document: Document) => {
  const sectionStart = Array.from(
    document.body.querySelectorAll("p, span, b, td"),
  ).find((element) => {
    const text = normalizeTextContent(element.textContent);
    return GWP_SECTION_TITLES.some(
      (title) => normalizeTextContent(title) === text,
    );
  });

  if (!sectionStart) {
    return;
  }

  let currentElement: Element | null =
    sectionStart.closest("table") || sectionStart;
  while (currentElement) {
    const nextElement: Element | null = currentElement.nextElementSibling;
    currentElement.remove();
    currentElement = nextElement;
  }

  while (
    document.body.lastElementChild &&
    !document.body.lastElementChild.textContent?.trim()
  ) {
    document.body.lastElementChild.remove();
  }
};

const extractBodyContent = (html: string, documentType?: TermsDocumentType) => {
  const parser = new DOMParser();
  const document = parser.parseFromString(html, "text/html");

  if (documentType === "gwp_terms") {
    stripTrailingGwpConsentSection(document);
  }

  return document.body?.innerHTML || html;
};

const splitConsentLabel = (label: string) => {
  const separatorIndex = label.indexOf(":");
  if (separatorIndex === -1) {
    return { heading: "", body: label };
  }

  return {
    heading: label.slice(0, separatorIndex + 1),
    body: label.slice(separatorIndex + 1).trim(),
  };
};

interface GwpConsentRowProps {
  checked: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}

const GwpConsentRow = ({ checked, label, onChange }: GwpConsentRowProps) => {
  const { heading, body } = splitConsentLabel(label);
  const palette = {
    border: "#1F4E79",
    checkboxBg: "#DEEAF1",
    contentBg: "#F5F9FC",
  };

  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch" }}>
        <Box
          sx={{
            width: 56,
            minWidth: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: palette.checkboxBg,
            borderRight: `1px solid ${palette.border}`,
          }}
        >
          <Checkbox
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            sx={{
              color: "#000000",
              "&.Mui-checked": {
                color: "#000000",
              },
            }}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            px: 1.5,
            py: 1.25,
            backgroundColor: palette.contentBg,
          }}
        >
          <Typography variant="body2">
            {heading && (
              <Box component="span" sx={{ fontWeight: 700 }}>
                {heading}{" "}
              </Box>
            )}
            {body}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

interface LanguageIconButtonsProps {
  language: TermsLanguage;
  onChange: (language: TermsLanguage) => void;
}

const LanguageIconButtons = ({
  language,
  onChange,
}: LanguageIconButtonsProps) => {
  return (
    <Stack direction="row" spacing={1} justifyContent="center">
      {TERMS_LANGUAGES.map((lang) => {
        const isSelected = language === lang;

        return (
          <IconButton
            key={lang}
            onClick={() => onChange(lang)}
            sx={{
              border: "1px solid",
              borderColor: isSelected ? "primary.main" : "divider",
              borderRadius: 1.5,
              backgroundColor: isSelected
                ? "action.selected"
                : "background.paper",
            }}
          >
            <img
              src={`https://flagcdn.com/w40/${languageMappping[lang]}.png`}
              alt={lang}
              style={{ width: 28, height: 28, objectFit: "contain" }}
            />
          </IconButton>
        );
      })}
    </Stack>
  );
};

const getApplicantDisplayName = (applicantDoc: ApplicantDoc | null) => {
  if (!applicantDoc) {
    return "";
  }

  return `${applicantDoc.applicant.name || ""} ${applicantDoc.applicant.surname || ""}`.trim();
};

const PrivacyPolicyPage = () => {
  const [step, setStep] = useState<PageStep>("email");
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState<TermsLanguage>("en");
  const [applicantDoc, setApplicantDoc] = useState<ApplicantDoc | null>(null);
  const [existingAcceptances, setExistingAcceptances] =
    useState<ExistingTermsAcceptances>({});
  const [acceptedChecks, setAcceptedChecks] = useState<
    Record<TermsDocumentType, boolean>
  >({
    gwp_terms: false,
    hops_privacy_policy: false,
  });
  const [gwpConsentChecks, setGwpConsentChecks] = useState<
    Record<GwpConsentKey, boolean>
  >({
    terms: false,
    dataProtection: false,
    govUkHelper: false,
    newServices: false,
  });
  const [htmlCache, setHtmlCache] = useState<Record<string, string>>({});
  const [isLookingUpApplicant, setIsLookingUpApplicant] = useState(false);
  const [isSavingAcceptance, setIsSavingAcceptance] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const [hasReachedDocumentEnd, setHasReachedDocumentEnd] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const documentEndRef = useRef<HTMLDivElement | null>(null);

  const copy = PAGE_COPY[language];

  const currentDocumentType = useMemo<TermsDocumentType | null>(() => {
    if (step === "gwp_terms" || step === "hops_privacy_policy") {
      return step;
    }

    return null;
  }, [step]);

  const currentDocumentKey = currentDocumentType
    ? `${currentDocumentType}_${language}`
    : "";
  const currentDocumentHtml = currentDocumentKey
    ? htmlCache[currentDocumentKey] || ""
    : "";

  useEffect(() => {
    if (!currentDocumentType) {
      return;
    }

    if (htmlCache[currentDocumentKey]) {
      setDocumentError("");
      return;
    }

    let isActive = true;

    const loadDocument = async () => {
      setIsLoadingDocument(true);
      setDocumentError("");

      try {
        const response = await fetch(
          getTermsHtmlPath(currentDocumentType, language),
        );
        if (!response.ok) {
          throw new Error(`Failed to load ${currentDocumentType}`);
        }

        const html = await response.text();
        if (!isActive) {
          return;
        }

        setHtmlCache((currentCache) => ({
          ...currentCache,
          [currentDocumentKey]: extractBodyContent(html, currentDocumentType),
        }));
      } catch (error) {
        if (isActive) {
          console.error(error);
          setDocumentError(copy.documentLoadError);
        }
      } finally {
        if (isActive) {
          setIsLoadingDocument(false);
        }
      }
    };

    loadDocument();

    return () => {
      isActive = false;
    };
  }, [
    copy.documentLoadError,
    currentDocumentKey,
    currentDocumentType,
    htmlCache,
    language,
  ]);

  useEffect(() => {
    setHasReachedDocumentEnd(false);
  }, [currentDocumentKey, currentDocumentType]);

  useEffect(() => {
    if (!currentDocumentType || isLoadingDocument) {
      return;
    }

    const documentEnd = documentEndRef.current;
    if (!documentEnd) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setHasReachedDocumentEnd(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        threshold: 0.2,
      },
    );

    observer.observe(documentEnd);

    return () => {
      observer.disconnect();
    };
  }, [currentDocumentKey, currentDocumentType, isLoadingDocument]);

  const handleLookup = async () => {
    setIsLookingUpApplicant(true);
    setLookupError("");
    setSubmitError("");

    try {
      const resolvedApplicant = await resolveTermsApplicant(email);
      if (!resolvedApplicant) {
        setApplicantDoc(null);
        setExistingAcceptances({});
        setLookupError(copy.emailNotFound);
        return;
      }

      const {
        applicantDoc: matchedApplicant,
        existingAcceptances: acceptances,
      } = resolvedApplicant;
      const applicantLanguage = resolveTermsLanguage(
        matchedApplicant.applicant.preferredLanguage,
      );

      setApplicantDoc(matchedApplicant);
      setExistingAcceptances(acceptances);
      setAcceptedChecks({
        gwp_terms: false,
        hops_privacy_policy: false,
      });
      setGwpConsentChecks({
        terms: false,
        dataProtection: false,
        govUkHelper: false,
        newServices: false,
      });
      setLanguage(applicantLanguage);
      setStep("hops_privacy_policy");
    } catch (error) {
      console.error(error);
      setLookupError(copy.genericError);
    } finally {
      setIsLookingUpApplicant(false);
    }
  };

  const handleAcceptDocument = async () => {
    if (!applicantDoc || !currentDocumentType) {
      return;
    }

    setIsSavingAcceptance(true);
    setSubmitError("");

    try {
      const acceptance = await submitTermsAcceptance({
        applicantId: applicantDoc.id,
        email: (applicantDoc.applicant.email || email)
          .trim()
          .toLocaleLowerCase(),
        documentType: currentDocumentType,
        language,
        optionalNewServicesAccepted:
          currentDocumentType === "gwp_terms"
            ? gwpConsentChecks.newServices
            : undefined,
      });
      setExistingAcceptances((currentAcceptances) => ({
        ...currentAcceptances,
        [currentDocumentType]: acceptance,
      }));

      if (currentDocumentType === "hops_privacy_policy") {
        setStep("gwp_terms");
      } else {
        setStep("done");
      }
    } catch (error) {
      console.error(error);
      setSubmitError(copy.acceptanceError);
    } finally {
      setIsSavingAcceptance(false);
    }
  };

  const currentAcceptance = currentDocumentType
    ? existingAcceptances[currentDocumentType]
    : undefined;
  const applicantName = getApplicantDisplayName(applicantDoc);
  const isCurrentDocumentAccepted =
    hasReachedDocumentEnd &&
    (currentDocumentType === "gwp_terms"
      ? REQUIRED_GWP_CONSENT_KEYS.every(
          (consentKey) => gwpConsentChecks[consentKey],
        )
      : currentDocumentType === "hops_privacy_policy"
        ? acceptedChecks.hops_privacy_policy
        : false);

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Paper sx={{ p: { xs: 3, md: 5 } }}>
        <Stack spacing={3} alignItems={step === "email" ? "center" : "stretch"}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: step === "email" ? "center" : "flex-start",
            }}
          >
            <LanguageIconButtons
              language={language}
              onChange={(newLanguage) => setLanguage(newLanguage)}
            />
          </Box>
          <Box sx={{ textAlign: step === "email" ? "center" : "left" }}>
            <Typography variant="h4" gutterBottom>
              {copy.pageTitle}
            </Typography>
            <Typography color="text.secondary">
              {copy.pageDescription}
            </Typography>
          </Box>

          {step === "email" && (
            <Stack spacing={3} maxWidth={520} width="100%" alignItems="center">
              <TextField
                label={copy.emailLabel}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                fullWidth
                autoFocus
                onKeyDown={(event) => {
                  if (event.key === "Enter" && email.trim()) {
                    handleLookup();
                  }
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {copy.emailHelp}
              </Typography>
              {lookupError && (
                <Alert severity="error" sx={{ width: "100%" }}>
                  {lookupError}
                </Alert>
              )}
              <Box sx={{ textAlign: "center" }}>
                <LoadingButton
                  variant="contained"
                  loading={isLookingUpApplicant}
                  onClick={handleLookup}
                  disabled={!email.trim()}
                >
                  {copy.emailButton}
                </LoadingButton>
              </Box>
            </Stack>
          )}

          {step !== "email" && step !== "done" && currentDocumentType && (
            <Stack spacing={3}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography variant="h5">
                    {TERMS_DOCUMENTS[currentDocumentType].title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {copy.recognizedApplicant}:{" "}
                    {applicantName || applicantDoc?.applicant.email}
                  </Typography>
                </Box>
              </Stack>

              {currentAcceptance && (
                <Alert severity="info">
                  {copy.acceptedEarlier}{" "}
                  {new Date(currentAcceptance.acceptedAt).toLocaleString()}
                  {currentDocumentType === "gwp_terms" && (
                    <>
                      <br />
                      Optional new services notification:{" "}
                      {currentAcceptance.optionalNewServicesAccepted
                        ? "yes"
                        : "no"}
                    </>
                  )}
                </Alert>
              )}

              {documentError && <Alert severity="error">{documentError}</Alert>}
              {submitError && <Alert severity="error">{submitError}</Alert>}

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, md: 3 },
                  bgcolor: "#fff",
                  "& table": {
                    width: "100%",
                    maxWidth: "100%",
                  },
                  "& img": {
                    maxWidth: "100%",
                  },
                }}
              >
                {isLoadingDocument ? (
                  <Typography color="text.secondary">
                    Loading document...
                  </Typography>
                ) : (
                  <Box
                    dangerouslySetInnerHTML={{ __html: currentDocumentHtml }}
                  />
                )}
              </Paper>
              {!isLoadingDocument && currentDocumentHtml && (
                <Box ref={documentEndRef} sx={{ height: 1 }} />
              )}

              {!hasReachedDocumentEnd && !isLoadingDocument && (
                <Alert severity="info">{copy.scrollToEndHint}</Alert>
              )}

              {currentDocumentType === "gwp_terms" ? (
                <Stack spacing={1}>
                  {REQUIRED_GWP_CONSENT_KEYS.map((consentKey) => (
                    <GwpConsentRow
                      key={consentKey}
                      checked={gwpConsentChecks[consentKey]}
                      onChange={(checked) =>
                        setGwpConsentChecks((currentChecks) => ({
                          ...currentChecks,
                          [consentKey]: checked,
                        }))
                      }
                      label={copy.gwpRequiredCheckboxes[consentKey]}
                    />
                  ))}
                  <GwpConsentRow
                    checked={gwpConsentChecks.newServices}
                    onChange={(checked) =>
                      setGwpConsentChecks((currentChecks) => ({
                        ...currentChecks,
                        newServices: checked,
                      }))
                    }
                    label={copy.gwpOptionalCheckbox}
                  />
                </Stack>
              ) : (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedChecks[currentDocumentType]}
                      onChange={(event) =>
                        setAcceptedChecks((currentChecks) => ({
                          ...currentChecks,
                          [currentDocumentType]: event.target.checked,
                        }))
                      }
                    />
                  }
                  label={copy.hopsCheckbox}
                />
              )}

              <Box>
                <LoadingButton
                  variant="contained"
                  loading={isSavingAcceptance}
                  onClick={handleAcceptDocument}
                  disabled={!isCurrentDocumentAccepted || isLoadingDocument}
                >
                  {currentDocumentType === "gwp_terms"
                    ? copy.finishButton
                    : copy.continueButton}
                </LoadingButton>
              </Box>
            </Stack>
          )}

          {step === "done" && (
            <Stack
              spacing={3}
              sx={{ py: { xs: 4, md: 8 }, textAlign: "center" }}
            >
              <Box>
                <Typography variant="h4" gutterBottom>
                  {copy.confirmationTitle}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {copy.confirmationDescription}
                </Typography>
                <Typography color="text.secondary">
                  {copy.confirmationCooperation}
                </Typography>
              </Box>
              <Alert severity="success" sx={{ textAlign: "left" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {copy.confirmationRecorded}
                </Typography>
                {TERMS_DOCUMENTS.gwp_terms.title}:{" "}
                {existingAcceptances.gwp_terms
                  ? new Date(
                      existingAcceptances.gwp_terms.acceptedAt,
                    ).toLocaleString()
                  : "-"}
                <br />
                {TERMS_DOCUMENTS.hops_privacy_policy.title}:{" "}
                {existingAcceptances.hops_privacy_policy
                  ? new Date(
                      existingAcceptances.hops_privacy_policy.acceptedAt,
                    ).toLocaleString()
                  : "-"}
              </Alert>
            </Stack>
          )}
        </Stack>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicyPage;
